/**
 * Wellness theme preset – warm, organic, rounded, nature-inspired.
 *
 * Designed for acupuncture, chiropractic, holistic, and integrative care
 * practices where warmth, trust, and a natural feel are the primary signals.
 * Key design decisions:
 * - Nunito (body) – rounded sans-serif letterforms match the organic feel
 * - Source Serif 4 (heading, weight 600) – modern serif, softer than bold
 * - Sage green primary – growth, nature, wellness
 * - Generous border radius throughout – nothing feels sharp or clinical
 * - `cardStyle: filled` – soft filled backgrounds feel nurturing
 * - `buttonStyle: pill` – fully rounded buttons feel approachable
 * - Wide sectionPadding (5rem) for an airy, unhurried layout
 *
 * AI-NOTE: This preset pairs with `heroStyle: gradient`. When generating pages
 * for wellness providers, use gradient heroes (already centered) with the sage
 * primary color gradient. The gradient hero produces a soft, nature-inspired
 * full-bleed treatment that fits wellness branding without requiring imagery.
 */

import { ThemeSchema, type Theme } from '../types';
import { DEFAULT_DENSITY } from './base';

export const wellnessTheme: Theme = ThemeSchema.parse({
  name: 'wellness',
  colors: {
    // Primary: Sage green – growth, nature, wellness
    primary: '#4E6D5C',
    primaryDark: '#3C5648',
    primaryLight: '#7A9C8A',
    // Accent: Warm terracotta – earthy complement to sage (darkened for AA contrast)
    accent: '#8F5A2F',
    accentDark: '#6E4220',
    accentLight: '#D4A478',
    // Surfaces
    background: '#FAFAF6',
    backgroundAlt: '#EBE8DE',
    surface: '#FFFFFF',
    // Text
    text: '#3A3D3B',
    textMuted: '#525755',
    textInverse: '#FFFFFF',
    // Borders
    border: '#E2DFD6',
    // Status (darkened for AA non-text contrast)
    success: '#4F7B53',
    warning: '#8B6F2E',
    error: '#9F3F3B',
    // Medical-specific (warm-toned variants)
    evidenceStrong: '#4F7B53',
    evidenceModerate: '#8B6F2E',
    evidenceLow: '#5C5C5C',
    safetyWarning: '#9F3F3B',
    safetyInfo: '#3A5C7D',
  },
  typography: {
    fontFamily: '"Nunito", -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
    fontFamilyHeading: '"Source Serif 4", Georgia, "Times New Roman", serif',
    fontWeightHeading: '600',
    fontSizeBase: '1rem',
    fontSizeSm: '0.875rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '1.875rem',
    fontSize4xl: '2.25rem',
    lineHeightBase: '1.7',
    lineHeightTight: '1.3',
    lineHeightLoose: '1.85',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },
  borderRadius: {
    none: '0',
    sm: '0.375rem',
    md: '0.625rem',
    lg: '1rem',
    xl: '1.5rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 3px 0 rgb(91 123 106 / 0.08)',
    md: '0 4px 6px -1px rgb(91 123 106 / 0.1), 0 2px 4px -2px rgb(91 123 106 / 0.08)',
    lg: '0 10px 15px -3px rgb(91 123 106 / 0.1), 0 4px 6px -4px rgb(91 123 106 / 0.08)',
    xl: '0 20px 25px -5px rgb(91 123 106 / 0.1), 0 8px 10px -6px rgb(91 123 106 / 0.08)',
  },
  layout: {
    heroStyle: 'gradient',
    cardStyle: 'filled',
    iconStyle: 'filled',
    buttonStyle: 'pill',
    sectionPadding: '5rem',
    contentMaxWidth: '60ch',
  },
  // HAL-495: shared layout rhythm + ch-based reading measures. See DEFAULT_DENSITY.
  density: DEFAULT_DENSITY,
});
